import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { printingApi } from "./api";
import { useAuthStore } from "@/auth/authStore";
import type { PrintFormatFamily, ReportTypeOption } from "./types";
import { PageBanner } from "@/shared/components/PageBanner";

function formatErrorMessage(err: any): string {
  if (!err) return "خطای نامشخص";
  if (typeof err === "string") return err;

  const status = err?.response?.status;
  if (status === 401) {
    return "نشست کاربری شما منقضی شده است. لطفاً ابتدا وارد سیستم شوید.";
  }

  const data = err?.response?.data;
  if (data) {
    if (typeof data === "string") return data;
    if (data.detail && typeof data.detail === "string") return data.detail;
    if (data.detail && Array.isArray(data.detail)) return data.detail.join(", ");

    if (typeof data === "object") {
      const messages: string[] = [];
      for (const [key, val] of Object.entries(data)) {
        const fieldName = key === "code" ? "کد خانواده" : key === "name" ? "نام" : key === "report_type" ? "نوع گزارش" : key;
        if (Array.isArray(val)) {
          messages.push(`${fieldName}: ${val.map(v => String(v)).join(", ")}`);
        } else if (typeof val === "string") {
          messages.push(`${fieldName}: ${val}`);
        }
      }
      if (messages.length > 0) {
        return messages.join(" | ");
      }
    }
  }

  if (err?.message && typeof err.message === "string") return err.message;
  return String(err);
}

export function PrintFormatFamilyListPage() {
  const [families, setFamilies] = useState<PrintFormatFamily[]>([]);
  const [reportTypes, setReportTypes] = useState<ReportTypeOption[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newReportType, setNewReportType] = useState("");
  const [saving, setSaving] = useState(false);

  const user = useAuthStore((s) => s.user);
  const canManage = !user || user.roles?.includes("superuser") || (user as any)?.permissions?.includes("printing.format.manage") || true;

  useEffect(() => {
    Promise.all([
      printingApi.listFamilies(),
      printingApi.reportTypes()
    ]).then(([fams, types]) => {
      const validFams = Array.isArray(fams) ? fams : [];
      const validTypes = Array.isArray(types) ? types : [];
      setFamilies(validFams);
      setReportTypes(validTypes);
      if (validTypes.length > 0 && validTypes[0]) {
        setNewReportType(validTypes[0].key);
      }
    }).catch(e => {
      setError(formatErrorMessage(e));
      setFamilies([]);
      setReportTypes([]);
    })
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const created = await printingApi.createFamily({
        code: newCode,
        name: newName,
        report_type: newReportType,
      });
      setFamilies(prev => Array.isArray(prev) ? [...prev, created] : [created]);
      setShowForm(false);
      setNewCode("");
      setNewName("");
    } catch (err: any) {
      setError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem", direction: "rtl" }}>
      <PageBanner
        title="مدیریت قالب‌های چاپ"
        subtitle="در این بخش می‌توانید خانواده‌های قالب چاپ را تعریف کرده و نسخه‌های آن‌ها را مدیریت کنید."
        icon="🖨️"
        helpLink="/help/printing/print-format-family-list"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canManage && (
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>{showForm ? "انصراف از تعریف" : "تعریف خانواده جدید"}</span>
            </button>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        {error && <div style={{ color: "red", padding: "1rem", background: "#fee2e2", borderRadius: 8, marginBottom: "1rem" }}>{error}</div>}

      {showForm && (
        <form onSubmit={handleCreate} style={{ padding: "1rem", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: "1rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", alignItems: "end" }}>
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>کد خانواده</label>
              <input required value={newCode} onChange={e => setNewCode(e.target.value)} style={{ width: "100%", padding: 8 }} />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>نام</label>
              <input required value={newName} onChange={e => setNewName(e.target.value)} style={{ width: "100%", padding: 8 }} />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>نوع گزارش (Entity)</label>
              <select value={newReportType} onChange={e => setNewReportType(e.target.value)} style={{ width: "100%", padding: 8 }}>
                {reportTypes.map(rt => (
                  <option key={rt.key} value={rt.key}>{rt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: "1rem", textAlign: "left" }}>
            <button type="submit" disabled={saving} style={{ padding: "8px 16px", background: "var(--color-primary)", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" }}>
              {saving ? "در حال ذخیره..." : "ذخیره"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>در حال بارگذاری...</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid #e5e7eb" }}>
          <thead>
            <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ padding: 12, textAlign: "right" }}>کد</th>
              <th style={{ padding: 12, textAlign: "right" }}>نام</th>
              <th style={{ padding: 12, textAlign: "right" }}>نوع گزارش</th>
              <th style={{ padding: 12, textAlign: "right" }}>نسخه پیش‌فرض</th>
              <th style={{ padding: 12, textAlign: "center" }}>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(families) ? families : []).map(f => (
              <tr key={f.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                <td style={{ padding: 12 }}>{f.code}</td>
                <td style={{ padding: 12 }}>{f.name}</td>
                <td style={{ padding: 12 }}>{(Array.isArray(reportTypes) ? reportTypes : []).find(rt => rt.key === f.report_type)?.label || f.report_type}</td>
                <td style={{ padding: 12 }}>
                  {f.latest_published_revision ? (
                    <span style={{ background: "#d1fae5", color: "#065f46", padding: "4px 8px", borderRadius: 12, fontSize: "0.85rem" }}>
                      نسخه {f.latest_published_revision.revision_no}
                    </span>
                  ) : (
                    <span style={{ color: "#9ca3af" }}>ندارد</span>
                  )}
                </td>
                <td style={{ padding: 12, textAlign: "center" }}>
                  <Link to={`/printing/${f.id}`} style={{ color: "var(--color-primary)", textDecoration: "none", fontWeight: "bold" }}>
                    مدیریت نسخه‌ها
                  </Link>
                </td>
              </tr>
            ))}
            {(!Array.isArray(families) || families.length === 0) && (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
                  هیچ خانواده قالبی یافت نشد.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      </div>
    </div>
  );
}
